import React, { useState, useEffect } from 'react';
import {
  HomefieldIconFulfillmentShippingFilled,
  HomefieldIconFulfillmentShippingOutlined,
  HomefieldIconFulfillmentPickupFilled,
  HomefieldIconFulfillmentPickupOutlined,
  HomefieldIconFulfillmentSamedayFilled,
  HomefieldIconFulfillmentSamedayOutlined,
} from '@dsg-tech/homefield-react-icons';
import { useAppSelector } from 'store/store';
import { storeDetailsSelector } from 'store/selectors/locationServicesSelector';
import {
  aosSelector,
  getFeatureFlagByNameSelector,
  isSkuSelectionUndefinedSelector,
} from 'store/selectors/commonSelector';
import { ActionText } from '@dsg-tech/homefield-react';
import useProductStock from '../../hooks/useProductStock';
import FulfillmentButton from './components/FulfillmentButton/FulfillmentButton';
import FulfillmentMessaging from './components/FulfillmentMessaging/FulfillmentMessaging';
import { QuickviewDetails, SkuDetails } from './types';

type Props = {
  quickviewDetails: QuickviewDetails;
  sku: SkuDetails;
  count: number;
  selectedMethod: string;
  setSelectedMethod: (value: string) => void;
};

type IconProps = {
  height?: string;
  width?: string;
};

const FulfillmentOptions = ({
  quickviewDetails,
  sku,
  count,
  selectedMethod,
  setSelectedMethod,
}: Props) => {
  const storeDetails = useAppSelector(storeDetailsSelector);
  const aos = useAppSelector(aosSelector);
  const sameDayDeliveryEnabled =
    useAppSelector(getFeatureFlagByNameSelector('sameDayDeliveryEnabled')) && !aos;
  const isSkuSelectionUndefined = useAppSelector(isSkuSelectionUndefinedSelector);
  const [activeOption, setActiveOption] = useState<string>('SHIP');
  // Used to store same day delivery availability data derived from sdd availability endpoint call

  const { shipButtonState, storeButtonState, sddButtonState } = useProductStock(
    sku,
    quickviewDetails,
    count,
  );

  // Used to store a single dimensional array of all the skus in the quickviewDetails data returned from quickview api
  const [skuList] = useState<string[]>(
    quickviewDetails?.skus ? quickviewDetails.skus.map((a) => a.partNumber) : [],
  );

  useEffect(() => {
    if (selectedMethod) {
      setActiveOption(selectedMethod);
    }
  }, [selectedMethod]);

  useEffect(() => {
    // Logic handles updating the selectedMethod used for Add to Cart when the activeOption is valid otherwise unselects it as a selectedMethod to disable the button
    if (activeOption === 'SHIP') {
      if (!shipButtonState.valid) {
        setSelectedMethod('');
      } else {
        setSelectedMethod(shipButtonState.type.toUpperCase());
      }
    } else if (['BOPIS', 'BOPL'].includes(activeOption)) {
      if (!storeButtonState.valid) {
        setSelectedMethod('');
      } else {
        setSelectedMethod(storeButtonState.type.toUpperCase());
      }
    } else if (sameDayDeliveryEnabled && activeOption === 'SDD') {
      if (!sddButtonState.valid) {
        setSelectedMethod('');
      } else {
        setSelectedMethod(sddButtonState.type.toUpperCase());
      }
    }
  }, [shipButtonState, storeButtonState, sddButtonState]);

  const handleOpenStoreModalService = (openModal = true) => {
    window.headerFunctions.storeSelection({
      ...(skuList && { skusArr: skuList }),
      ...(sku?.partNumber && { selectedSkuVal: sku.partNumber }),
      ...(!openModal && { openModal: false }),
    });
  };

  useEffect(() => {
    if (skuList) {
      // Handle updating the store list data when skuList changes
      handleOpenStoreModalService(false);
    }
  }, [skuList]);

  const isPickupSelected = activeOption === 'BOPIS' || activeOption === 'BOPL';
  const isPickUpNotAvailable = isSkuSelectionUndefined || storeButtonState.disabled;
  const isSDDNotAvailable = isSkuSelectionUndefined || sddButtonState.disabled;
  const isShippingNotAvailable = isSkuSelectionUndefined || shipButtonState.disabled;

  const setIcon = (
    isSelected: boolean,
    isAvailable: boolean,
    iconFilled: React.ComponentType<IconProps>,
    iconOutlined: React.ComponentType<IconProps>,
  ) => {
    const Icon = isSelected && !isAvailable ? iconFilled : iconOutlined;
    return <Icon height="36px" width="36px" />;
  };

  return (
    <div className="FulfillmentOptions" aria-labelledby="FulfillmentOptions-label">
      <div className="hmf-display-flex hmf-flex-row hmf-justify-content-between hmf-align-items-center">
        <p
          className="hmf-body-bold-m hmf-text-transform-capitalize label-base hmf-m-0"
          id="FulfillmentOptions-label">
          How to Get It
        </p>
        <p className="hmf-body-m hmf-m-0 label-base">
          My Store &bull;{' '}
          <ActionText
            aria-label={
              storeDetails?.name
                ? `Your active store is ${storeDetails.name}, click to change`
                : 'No active store, click to select a store for buy online and pickup in store availability'
            }
            variant="primary"
            className="hmf-body-bold-m hmf-text-transform-capitalize label-base"
            size="medium"
            onClick={() => handleOpenStoreModalService(true)}>
            {storeDetails?.name ? storeDetails.name.toLowerCase() : 'Select Store'}
          </ActionText>
        </p>
      </div>
      <div
        className="hmf-display-flex hmf-flex-row qv-fulfillmentButton hmf-my-xs"
        role="radiogroup">
        <FulfillmentButton
          type="ship"
          className="FulfillmentMessaging-ship"
          icon={setIcon(
            activeOption === 'SHIP',
            isShippingNotAvailable,
            HomefieldIconFulfillmentShippingFilled,
            HomefieldIconFulfillmentShippingOutlined,
          )}
          title={shipButtonState.title}
          subText={shipButtonState.subText}
          disabled={isShippingNotAvailable}
          selected={activeOption === 'SHIP'}
          onClick={() => {
            const method = shipButtonState.type.toUpperCase();
            setActiveOption(method);
            if (shipButtonState.valid) {
              setSelectedMethod(method);
            } else {
              setSelectedMethod('');
            }
          }}
        />
        <FulfillmentButton
          type="store"
          className="hmf-ml-xs FulfillmentMessaging-store"
          icon={setIcon(
            isPickupSelected,
            isPickUpNotAvailable,
            HomefieldIconFulfillmentPickupFilled,
            HomefieldIconFulfillmentPickupOutlined,
          )}
          title={storeButtonState.title}
          subText={storeButtonState.subText}
          disabled={isPickUpNotAvailable}
          selected={isPickupSelected}
          onClick={() => {
            const method = storeButtonState.type.toUpperCase();
            setActiveOption(method);
            if (storeButtonState.valid) {
              setSelectedMethod(method);
            } else {
              setSelectedMethod('');
            }
          }}
        />
        {sameDayDeliveryEnabled && (
          <FulfillmentButton
            type="sdd"
            className="hmf-ml-xs FulfillmentMessaging-sdd"
            icon={setIcon(
              activeOption === 'SDD',
              isSDDNotAvailable,
              HomefieldIconFulfillmentSamedayFilled,
              HomefieldIconFulfillmentSamedayOutlined,
            )}
            title={sddButtonState.title}
            subText={sddButtonState.subText}
            disabled={isSDDNotAvailable}
            selected={activeOption === 'SDD'}
            onClick={() => {
              const method = sddButtonState.type.toUpperCase();
              setActiveOption(method);
              if (sddButtonState.valid) {
                setSelectedMethod(method);
              } else {
                setSelectedMethod('');
              }
            }}
          />
        )}
      </div>
      <FulfillmentMessaging
        activeOption={activeOption}
        shipButtonState={shipButtonState}
        storeButtonState={storeButtonState}
        sddButtonState={sddButtonState}
        selectedStore={storeDetails}
        skuList={skuList}
        sku={sku}
      />
    </div>
  );
};

export default FulfillmentOptions;
